import React, { useEffect, useState, useRef,useMemo } from 'react';
import ReactPlayerLoader from "@brightcove/react-player-loader";
import VideoLoadingState from '../common/VideoLoadingState.jsx';
import VideoErrorState from '../common/VideoErrorState.jsx';
import VideoProgressBar from '../common/VideoProgressBar.jsx';
import FeatureCoachmark from '../common/FeatureCoachmark';
import { __ } from '../../Lang.js';

const PlayerSection = ({
  video,
  playlist = null,
  playlistAcl = {},
  usePlaylistOverride = false,
  video_time,
  brightcoveConfig,
  restricted,
  block_message,
  usage_type_link,
  usage_type_message,
  cta_kind,
  app_link,
  app_button_title,
  autoplay,
  shuffle,
  repeat,
  setCountdown,
  setShowOverlay,
  countdown,
  showOverlay,
  loading,
  error,
  setError,
  setLoading,
  navigate,
  relatedVideos,
  logged_in,
  results_search,
  activeTab,
  context,
  svod_subscription,           
  lang = 'en'
}) => {
  const brightcoveAccountId = brightcoveConfig?.account_id;
  const brightcovePlayerId  = brightcoveConfig?.player_id;
  const brightcovePlaylistPlayerId  = brightcoveConfig?.playlist_player_id;
  const brightcoveDefaultPlaylistId  = brightcoveConfig?.default_playlist_id;
  const timerInterval = useRef(null);
  const firstPlayRef = useRef(true);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const repeatRef = useRef(repeat);
  const shuffleRef = useRef(shuffle);
  const autoplayRef = useRef(autoplay);

  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);

  // Toggle Brightcove autoadvance when autoplay setting changes
  useEffect(() => {
    const player = playerRef.current;
    if (player && typeof player.playlist?.autoadvance === 'function') {
      // autoadvance(0) = immediate, autoadvance(null) = disabled
      player.playlist.autoadvance(autoplay ? 0 : null);
    }
  }, [autoplay]);
  useEffect(() => { firstPlayRef.current = true; }, [video?.id, playlist?.uniqid]);

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
    };
  }, []);


// guards against bfcache issues //
const [nonce, setNonce] = useState(0);

const firstAllowedRef = useMemo(() => {
  const list = JSON.parse(document.getElementById('video-show').dataset.group || '{}');
  const perms = playlistAcl || {};
  const order = list?.videos?.map(v => v.reference_id) || [];
  return order.find(r => perms?.[r]?.allowed) || null;
}, [playlistAcl]);

useEffect(() => {
  const onShow = (e) => {
    if (!e.persisted) return;
    const ref = video?.reference_id;
    const acl = playlistAcl?.[ref] ?? {allowed: false};

    if (!acl?.allowed) {
      const safeRef = firstAllowedRef || video?.reference_id;
      const safe = video?.permalinks?.web ?? `/app/twi/watch?v=${encodeURIComponent(safeRef)}${playlist?.uniqid ? `&list=${playlist.uniqid}` : ''}`;
      window.location.replace(safe);
      return;
    }
    setNonce(n => n + 1);
  };

  window.addEventListener('pageshow', onShow);
  return () => window.removeEventListener('pageshow', onShow);
}, [playlistAcl, firstAllowedRef, video?.reference_id, playlist?.uniqid]);


// --- inside VideoPlayerSection ---

const hasSub = !!svod_subscription;
const isUrl = (s) => typeof s === 'string' && (/^https?:\/\//.test(s) || s.startsWith('/'));
const hasHtml = (s) => typeof s === 'string' && /<[^>]+>/.test(s);

// The backend brokers BOTH the decision (cta_kind) and the copy (usage_type_message);
// each client owns its own login route. A missing cta_kind (e.g. a stale cached
// payload) falls back to the pre-cta_kind logic so a block never loses its button.
const resolvedCta = cta_kind ?? (!logged_in ? 'login' : (!hasSub ? 'learn_more' : 'none'));

let finalBlockMessage = usage_type_message || block_message || __('This content is only available to TWI subscribers.');
let ctaHref = null;
let ctaLabel = null;

// The usage info link is an HTML fragment (embedded anchor) → part of the message;
// a bare URL is a CTA target, never message copy.
if (restricted && usage_type_link && !isUrl(usage_type_link)) {
  finalBlockMessage = `${finalBlockMessage} ${usage_type_link}`.trim();
}

if (restricted) {
  switch (resolvedCta) {
    case 'login':
      ctaHref  = `/app/students/login?previous_url=/app/twi/watch?v=${video?.uniqid || ''}`;
      ctaLabel = __('Log In to Watch');
      break;
    case 'learn_more':
      ctaHref  = app_link || (isUrl(usage_type_link) ? usage_type_link : null);
      ctaLabel = app_button_title ? __(app_button_title) : __('Learn more');
      break;
    // 'none': message only — any embedded "HERE" link stays in the copy, no button.
  }
}

// sanitize message: strip any stray bare URLs so they don't render under the H2
const stripBareUrls = (s) =>
  typeof s === 'string' ? s.replace(/(https?:\/\/\S+|\/[^\s<]+)/g, '').trim() : s;

if (restricted) {
  const displayMessage = hasHtml(finalBlockMessage)
    ? finalBlockMessage
    : stripBareUrls(finalBlockMessage);

  return (
    <div className="video-right">
      <div className="video-restricted">
        {hasHtml(displayMessage) ? (
          <h2 dangerouslySetInnerHTML={{ __html: displayMessage }} />
        ) : (
          <h2>{displayMessage}</h2>
        )}

        {ctaHref && ctaLabel && (
          <a href={ctaHref} className="login-button">{ctaLabel}</a>
        )}
      </div>
    </div>
  );
}



  if (!brightcoveAccountId || !brightcovePlayerId) {
    return (
      <div className="video-right">
        <VideoErrorState error={__('Missing Brightcove configuration.')} />
      </div>
    );
  }

  return (
    <div className="video-right">
      <ReactPlayerLoader
        key={`${video.uniqid}-${playlist?.uniqid || 'single'}-${nonce}`}
        accountId={brightcoveAccountId}
        playerId={playlist?.uniqid ? String(brightcovePlaylistPlayerId) : String(brightcovePlayerId)}
        embedId="default"
        controls
        attrs={{
          className: "vjs-playlist-player-container",
          /* 'data-setup': JSON.stringify({ catalog: { video: null, playlist: null } }), */
        }}
        className="vjs-16-9"
        onEmbedCreated={() => setLoading(true)}
        onError={() => {
          setLoading(false);
          setError(__('Error loading video player'));
        }}
        onSuccess={(e) => {
          const player = e.ref;
          playerRef.current = player; // Store player for autoplay toggle
          // this bubbles up the player and playlist to e.g. navigation
          // Use video.video_time (from the video object) if available, otherwise fall back to video_time prop
          let resumeAt = video?.video_time ?? video_time;
          let navigating = false;
          let initialPlaylistItem = true; // don't URL-sync the first item; the server already placed us on it
          setLoading(true);

          if (!player) {
            setError(__('Error initializing video player'));
            return;
          }

          // Resolve a Brightcove video from either numeric ID or reference_id
          const loadVideoByIdOrRef = (player, val) => {
            return new Promise((resolve, reject) => {
              if (val == null) return reject(new Error('Empty id/ref'));
              const s = String(val).trim();
              const isNumericId = /^\d+$/.test(s);

              // Prefer the dedicated APIs to avoid mistakes
              if (isNumericId) {
                player.catalog.getVideo(s, (err, v) => err ? reject(err) : resolve(v));
              } else {
                // Reference IDs must be prefixed with `ref:`
                player.catalog.getVideo('ref:' + s, (err, v) => {
                  // Some data sources may hand us a stray numeric-looking string without leading zeros.
                  // If ref lookup failed and it *is* numeric, try as id once.
                  if (err && /^\d+$/.test(s)) {
                    return player.catalog.getVideo(s, (err2, v2) => err2 ? reject(err) : resolve(v2));
                  }
                  return err ? reject(err) : resolve(v);
                });
              }
            });
          };

          const loadUi = () => {
            if (typeof player.bcPlaylistUi === 'function') {
              player.bcPlaylistUi();
            }
            setLoading(false);
          };

          if (usePlaylistOverride && Array.isArray(playlist?.videos) && playlist.videos.length) {
            (async () => {
              try {
                setLoading(true);
                const items = playlist.videos;

                // Load each item by reference_id (canonical uniqid) — never by the raw
                // Brightcove id. Use allSettled so a single 404 (BC-deleted video still in
                // DB) doesn't kill the whole list.
                const settled = await Promise.allSettled(
                  items.map(({ uniqid }) => loadVideoByIdOrRef(player, uniqid))
                );
                // Keep each loaded Brightcove video paired with the source item it came from,
                // so the start index can be matched against OUR data even if Brightcove returns
                // an unexpected reference_id.
                const loaded = [];
                settled.forEach((r, i) => { if (r.status === 'fulfilled') loaded.push({ v: r.value, item: items[i] }); });
                const vids = loaded.map(x => x.v);
                if (vids.length === 0) throw new Error('No playable videos in playlist');
                const skipped = settled.length - vids.length;
                if (skipped > 0) console.warn(`Skipped ${skipped} unavailable video(s) in playlist`);

                // Resolve the start index for the page's target video. Match by our own uniqid
                // first (most reliable), then by the Brightcove reference_id.
                let idx = loaded.findIndex(x =>
                  x.item?.uniqid === video?.uniqid ||
                  x.v?.reference_id === video?.uniqid
                );
                if (idx < 0) {
                  console.warn('Target video not found in loaded playlist; defaulting to first item', { v: video?.uniqid });
                  idx = 0;
                }

                // Load the TARGET as the initial playlist item in one call. The two-arg form
                // avoids the item-0-then-currentItem race, where autoplay-on-first-loadedmetadata
                // could start track 0 before a separate currentItem(idx) swap settled.
                player.playlist(vids, idx);
                if (player.playlist?.autoadvance) player.playlist.autoadvance(autoplayRef.current ? 0 : null);
              } catch (e) {
                console.warn('Override playlist load failed:', e);
                setError(__('Error building playlist'));
              } finally {
                loadUi();
              }

            })();
          } else if (video?.uniqid) {
            // Single video (no playlist context): load by reference_id (canonical uniqid),
            // so we never pass the raw Brightcove id.
            (async () => {
              try {
                setLoading(true);
                const v = await loadVideoByIdOrRef(player, video.uniqid);
                if (typeof player.catalog?.load === 'function') {
                  player.catalog.load(v);
                }
              } catch (e) {
                console.warn('Single video load failed:', e);
                setError(__('Error loading video'));
              } finally {
                loadUi();
              }
            })();
          } else {
            loadUi();
          }



          let hasSeenLoadedMetadata = false;
          player.on('loadedmetadata', () => {
            if (typeof player.playlist === 'function') {
              if (typeof playlist !== undefined) {
                player.playlist().uniqid = playlist?.uniqid ?? player.playlist().reference_id;
              }
            }

            // Enable autoplay for all videos when autoplay is on
            if (firstPlayRef.current && autoplayRef.current) {
              player.play().catch(err => {
                console.warn('Autoplay failed (browser policy):', err);
              });
            }

            // Enable/disable Brightcove autoadvance for playlists based on autoplay setting
            if (typeof player.playlist?.autoadvance === 'function') {
              player.playlist.autoadvance(autoplayRef.current ? 0 : null);
            }

            // Seek to saved position on first loadedmetadata for this player instance
            if (!hasSeenLoadedMetadata) {
                hasSeenLoadedMetadata = true;
                const duration = player.duration();
                const resumeAtMs = Number(resumeAt) || 0;
                if (Number.isFinite(duration) && resumeAtMs > 0 && resumeAtMs < (duration - 10) * 1000) {
                  player.currentTime(resumeAtMs / 1000);
                }
            }
          });

          const startTracking = () => {
            if (!timerInterval.current) {
              timerInterval.current = setInterval(sendUpdate, 10000);
            }
          };

          const stopTracking = () => {
            if (timerInterval.current) {
              clearInterval(timerInterval.current);
              timerInterval.current = null;
            }
            sendUpdate();
          };

          const sendUpdate = () => {
            if (!player || typeof player.currentTime !== 'function') {
              return;
            }
            try {
              const currentTime = player.currentTime();
              if (currentTime < 5) return;

              fetch('/api/v2/twi/positionUpdate/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                  uniqid: String(video.uniqid),
                  milliseconds: String(Math.floor(currentTime * 1000))
                }),
                credentials: 'same-origin'
              }).catch(() => {});
            } catch {}
          };

          player.on('beforeplaylistitem', function() {
            try { player.pause?.(); } catch {}
            stopTracking();
          })
          

          player.on('playlistitem', function() {
            player.one('loadedmetadata', () => {
              startTracking();
              if (navigating) return;
              navigating = true;
              let videoIndex = player.playlist?.currentItem();
              let bcVideo = player.playlist()[videoIndex];
              let bcRef = bcVideo?.reference_id;
              // Map the now-playing Brightcove item back to OUR playlist video by reference_id
              // (canonical uniqid), NOT by index: the player playlist may be a filtered subset of
              // playlist.videos, so indices don't line up and indexing by them navigates to the
              // wrong (first) item.
              let ourVideo = playlist?.videos?.find(pv => pv.uniqid === bcRef)
                          ?? playlist?.videos?.[videoIndex];
              let navRef = ourVideo?.uniqid || '';

              // ACL is keyed by our canonical reference_id (uniqid), so look it up by navRef.
              let acl = playlistAcl?.[navRef] ?? playlistAcl?.[bcRef] ?? {allowed: false};
              if (acl.allowed === false) {
                try { player.stop?.(); } catch {}
                setLoading(true);
              }

              // Only sync the URL on a genuine advance (autoadvance / user click) to a DIFFERENT
              // video. Never on the initial item: the server already placed us on the correct
              // canonical URL, so navigating there causes a spurious reload / first-item loop.
              let v = new URL(window.location.href).searchParams.get('v');
              if (!initialPlaylistItem && navRef && v !== navRef) {
                let nextUrl = new URL(ourVideo?.permalinks?.web || window.location.href);
                nextUrl.searchParams.set('v', navRef);
                window.location.assign(nextUrl);
                return; // page is reloading
              }
              initialPlaylistItem = false;
              navigating = false;
            });
          });

          player.on(['play'], () => {firstPlayRef.current = false; startTracking(); setPlaying(true); });
          player.on(['pause', 'ended', 'unload'], () => {setPlaying(false); stopTracking()});

          player.on('dispose', () => {
            if (timerInterval.current) {
              clearInterval(timerInterval.current);
              timerInterval.current = null;
            }
          });

          player.on('ended', () => {
            if (repeatRef.current) {
              // Repeat current video (works for both single and playlist)
              if (player && typeof player.currentTime === 'function') {
                player.currentTime(0);
                player.play().catch(() => {});
              }
              return;
            }
            if (autoplayRef.current) {
              // Check if we're in a custom playlist (mylist parameter)
              const urlParams = new URLSearchParams(window.location.search);
              const hasMylist = urlParams.has('mylist');

              // For custom playlists, always use relatedVideos (which contains the playlist videos)
              // For other contexts, use relatedVideos as usual
              const autoplayIndex = relatedVideos.findIndex(v => v.uniqid === video.uniqid);
              const nextIndex = autoplayIndex + 1;
              let nextVideo;

              if (shuffleRef.current) {
                player.playlist?.shuffle();
                const availableVideos = relatedVideos.filter(v => v.uniqid !== video.uniqid);
                nextVideo = availableVideos[Math.floor(Math.random() * availableVideos.length)];
              } else {
                player.playlist?.sort();
                nextVideo = relatedVideos[nextIndex];
              }

              if (nextVideo) {
                // Build next URL, preserving mylist parameter if present
                let nextUrl = nextVideo.permalinks?.web;
                if (hasMylist) {
                  const mylistId = urlParams.get('mylist');
                  const separator = nextUrl.includes('?') ? '&' : '?';
                  nextUrl = `${nextUrl}${separator}mylist=${mylistId}`;
                }

                setCountdown(3);
                setShowOverlay(true);
                const interval = setInterval(() => {
                  setCountdown(prev => {
                    if (prev <= 1) {
                      clearInterval(interval);
                      setShowOverlay(false);
                      // Navigate after state updates complete
                      setTimeout(() => {
                        window.location.assign(nextUrl);
                      }, 0);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              }
            }
          });
        }}
      />

      {/* use localStorage.removeItem('seen_new_playlist_sidebar'); to reset for debugging */}
      {playlist && (
        <FeatureCoachmark
          anchorSelector=".vjs-playlist-player-container .vjs-playlist-sidebar .vjs-playlist-show-hide button"
          storageKey="seen_new_playlist_sidebar"
          title={__("New playlist sidebar")}
          body={__("Click here to open or close the new slide-out playlist")}
          ctaLabel={__("OK")}
          onCta={() => {
            const el = document.querySelector(
                  ".vjs-playlist-player-container .vjs-playlist-sidebar .vjs-playlist-show-hide button"
                );
                el?.click();
          }}
          placement="left-start"
        />
      )}


      {video.video_time > 0 && video.duration > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '4px 8px',
          pointerEvents: 'none',
          zIndex: 3,
          visibility: (playing || showOverlay || loading || error) ? 'hidden' : 'visible'
        }}
        >
          <VideoProgressBar video_time={video.video_time} duration={video.duration} height={6} />
        </div>
      )}



      <div className={`video-overlay ${showOverlay || loading || error ? 'visible' : ''}`}>
        {showOverlay && <span>{__('Next video playing in')} {countdown}...</span>}
        {loading && <VideoLoadingState />}
        {error && <VideoErrorState error={error} />}
      </div>
    </div>
  );
};

export default PlayerSection;